/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BackupRestoreApiService } from '../../../../shared/http';
import { BaseComponent } from '../../../../../../core/components';
import { ChangeDetectorRef, Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LbErrorService } from '../../../../shared/services/components';
import { LogService, RbacService } from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-upload-backup',
    templateUrl: './upload-backup.component.html',
    styleUrls: ['./upload-backup.component.scss']
})
export class UploadBackupComponent extends BaseComponent {
    /**
     * Define our form group.
     */
    public uploadForm: FormGroup;

    /**
     * Store incoming label
     * State of the file input.
     */
    public inputReset: boolean = false;

    /**
     * To store the file information.
     */
    public file: File;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private backupRestoreApiService: BackupRestoreApiService,
        private changeDetectorRef: ChangeDetectorRef,
        private formBuilder: FormBuilder,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();
        this.setUpForm();
        this.registerButtons();
    }

    /**
     * Read the file & check the extension type to ensure it is correct.
     * @param event
     */
    public onFileChange(event: any): void {
        this.logService.debug('onFileChange', 'start');
        const fileList: FileList = event.target.files;
        if (!fileList.length) {
            this.logService.error('backupRestore: onFileChange', 'No file retrieved from the browser event');

            return;
        }
        this.inputReset = false;
        this.file = fileList[0];
        this.uploadForm.get('file').setValue(this.file.name);
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Submit backup upload.
     */
    public submitUpload(): Observable<any> {
        return new Observable((observer) => {
            const formData: FormData = new FormData();
            formData.append('backup', this.file, this.file.name);
            this.backupRestoreApiService.uploadBackup(formData)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                        this.router.navigate(['../'], { relativeTo: this.route }).then().catch();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(formData, error.error.errors);
                        this.logService.error('BackupRestoreComponent: submitUpload', 'Error submitting data', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up our form.
     * @private
     */
    private setUpForm(): void {
        this.uploadForm = this.formBuilder.group({
            file: [null, Validators.required]
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'backupRestore-upload-modal-upload': [{ path: '/backup/upload', methods: ['POST'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['backupRestore-upload-modal-upload']) {
            this.uploadForm.disable();
        }
    }
}
